import type {
    ApiResponse,
    ApiPaginatedResponse,
    SiteSettings,
    HomePageData,
    AboutPageData,
    LeadershipPageData,
    BlogIndexPageData,
    BlogShowPageData,
    ContactFormData,
    User,
    AdminDashboardData,
    Post,
    Page,
    Experience,
    Achievement,
    Media,
    ContactSubmission,
    AuditLog
} from '../types/api';

const API_BASE = '/api/v1';

class ApiService {
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const headers = new Headers(options.headers || {});
        if (!headers.has('Accept')) {
            headers.set('Accept', 'application/json');
        }
        if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }

        const config: RequestInit = {
            ...options,
            headers,
            credentials: 'include',
        };

        const response = await fetch(`${API_BASE}${endpoint}`, config);

        if (response.status === 204) {
            return {} as T;
        }

        const data = await response.json().catch(() => null);

        if (!response.ok) {
            const errorMsg = data?.message || `Request failed with status ${response.status}`;
            const error = new Error(errorMsg) as Error & { status: number; errors?: Record<string, string[]> };
            error.status = response.status;
            error.errors = data?.errors;
            throw error;
        }

        return data as T;
    }

    // Public Endpoints
    async getSettings(): Promise<ApiResponse<SiteSettings>> {
        return this.request<ApiResponse<SiteSettings>>('/settings');
    }

    async getHome(): Promise<ApiResponse<HomePageData>> {
        return this.request<ApiResponse<HomePageData>>('/home');
    }

    async getAbout(): Promise<ApiResponse<AboutPageData>> {
        return this.request<ApiResponse<AboutPageData>>('/about');
    }

    async getLeadership(): Promise<ApiResponse<LeadershipPageData>> {
        return this.request<ApiResponse<LeadershipPageData>>('/leadership');
    }

    async getPosts(params: { category?: string; tag?: string; search?: string; page?: number } = {}): Promise<BlogIndexPageData> {
        const searchParams = new URLSearchParams();
        if (params.category) searchParams.set('category', params.category);
        if (params.tag) searchParams.set('tag', params.tag);
        if (params.search) searchParams.set('search', params.search);
        if (params.page) searchParams.set('page', String(params.page));

        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const res = await this.request<any>(`/posts${query}`);
        return {
            posts: res.data || [],
            categories: res.categories || [],
            tags: res.tags || [],
            featuredPost: res.featured_post || null,
            currentSearch: res.current_search || null,
            currentCategory: res.current_category || null,
            currentTag: res.current_tag || null,
            settings: res.settings || {},
            pagination: res.meta || { current_page: 1, last_page: 1, total: res.data?.length || 0, per_page: 12, from: 1, to: res.data?.length || 0, path: '' }
        };
    }

    async getPost(slug: string): Promise<ApiResponse<BlogShowPageData>> {
        return this.request<ApiResponse<BlogShowPageData>>(`/posts/${encodeURIComponent(slug)}`);
    }

    async submitContact(formData: ContactFormData): Promise<ApiResponse<{ id: number }>> {
        return this.request<ApiResponse<{ id: number }>>('/contact', {
            method: 'POST',
            body: JSON.stringify(formData),
        });
    }

    // Admin Authentication Endpoints
    async adminLogin(credentials: { email: string; password: string; remember?: boolean }): Promise<ApiResponse<{ user: User }>> {
        return this.request<ApiResponse<{ user: User }>>('/admin/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
    }

    async adminLogout(): Promise<ApiResponse<null>> {
        return this.request<ApiResponse<null>>('/admin/auth/logout', {
            method: 'POST',
        });
    }

    async adminGetMe(): Promise<ApiResponse<{ user: User }>> {
        return this.request<ApiResponse<{ user: User }>>('/admin/auth/me');
    }

    // Admin Dashboard
    async adminGetDashboard(): Promise<ApiResponse<AdminDashboardData>> {
        return this.request<ApiResponse<AdminDashboardData>>('/admin/dashboard');
    }

    // Admin Posts
    async adminGetPosts(page: number = 1): Promise<ApiPaginatedResponse<Post>> {
        return this.request<ApiPaginatedResponse<Post>>(`/admin/posts?page=${page}`);
    }

    async adminGetPost(id: number): Promise<ApiResponse<{ post: Post; categories: any[]; tags: any[] }>> {
        return this.request<ApiResponse<{ post: Post; categories: any[]; tags: any[] }>>(`/admin/posts/${id}`);
    }

    async adminSavePost(id: number | null, data: Partial<Post>): Promise<ApiResponse<{ post: Post }>> {
        return this.request<ApiResponse<{ post: Post }>>(id ? `/admin/posts/${id}` : '/admin/posts', {
            method: id ? 'PUT' : 'POST',
            body: JSON.stringify(data),
        });
    }

    async adminDeletePost(id: number): Promise<ApiResponse<null>> {
        return this.request<ApiResponse<null>>(`/admin/posts/${id}`, {
            method: 'DELETE',
        });
    }

    // Admin Pages
    async adminGetPages(): Promise<ApiResponse<Page[]>> {
        return this.request<ApiResponse<Page[]>>('/admin/pages');
    }

    async adminGetPage(id: number): Promise<ApiResponse<{ page: Page }>> {
        return this.request<ApiResponse<{ page: Page }>>(`/admin/pages/${id}`);
    }

    async adminSavePage(id: number | null, data: Partial<Page>): Promise<ApiResponse<{ page: Page }>> {
        return this.request<ApiResponse<{ page: Page }>>(id ? `/admin/pages/${id}` : '/admin/pages', {
            method: id ? 'PUT' : 'POST',
            body: JSON.stringify(data),
        });
    }

    async adminDeletePage(id: number): Promise<ApiResponse<null>> {
        return this.request<ApiResponse<null>>(`/admin/pages/${id}`, {
            method: 'DELETE',
        });
    }

    // Admin Experiences
    async adminGetExperiences(): Promise<ApiResponse<Experience[]>> {
        return this.request<ApiResponse<Experience[]>>('/admin/experiences');
    }

    async adminSaveExperience(id: number | null, data: Partial<Experience>): Promise<ApiResponse<{ experience: Experience }>> {
        return this.request<ApiResponse<{ experience: Experience }>>(id ? `/admin/experiences/${id}` : '/admin/experiences', {
            method: id ? 'PUT' : 'POST',
            body: JSON.stringify(data),
        });
    }

    async adminDeleteExperience(id: number): Promise<ApiResponse<null>> {
        return this.request<ApiResponse<null>>(`/admin/experiences/${id}`, {
            method: 'DELETE',
        });
    }

    // Admin Achievements
    async adminGetAchievements(): Promise<ApiResponse<Achievement[]>> {
        return this.request<ApiResponse<Achievement[]>>('/admin/achievements');
    }

    async adminSaveAchievement(id: number | null, data: Partial<Achievement>): Promise<ApiResponse<{ achievement: Achievement }>> {
        return this.request<ApiResponse<{ achievement: Achievement }>>(id ? `/admin/achievements/${id}` : '/admin/achievements', {
            method: id ? 'PUT' : 'POST',
            body: JSON.stringify(data),
        });
    }

    async adminDeleteAchievement(id: number): Promise<ApiResponse<null>> {
        return this.request<ApiResponse<null>>(`/admin/achievements/${id}`, {
            method: 'DELETE',
        });
    }

    // Admin Media
    async adminGetMedia(page: number = 1): Promise<ApiPaginatedResponse<Media>> {
        return this.request<ApiPaginatedResponse<Media>>(`/admin/media?page=${page}`);
    }

    async adminUploadMedia(payload: {
        filename: string;
        original_name: string;
        data: string;
        width?: number;
        height?: number;
        size_bytes?: number;
        alt_text?: string;
    } | FormData): Promise<ApiResponse<{ media: Media }>> {
        const isFormData = payload instanceof FormData;
        return this.request<ApiResponse<{ media: Media }>>('/admin/media', {
            method: 'POST',
            body: isFormData ? payload : JSON.stringify(payload),
        });
    }

    async adminDeleteMedia(id: number): Promise<ApiResponse<null>> {
        return this.request<ApiResponse<null>>(`/admin/media/${id}`, {
            method: 'DELETE',
        });
    }

    // Admin Contact Submissions
    async adminGetContacts(status?: string, page: number = 1): Promise<ApiPaginatedResponse<ContactSubmission>> {
        const query = status ? `?status=${status}&page=${page}` : `?page=${page}`;
        return this.request<ApiPaginatedResponse<ContactSubmission>>(`/admin/contact${query}`);
    }

    async adminUpdateContactStatus(id: number, status: 'read' | 'unread' | 'archived'): Promise<ApiResponse<{ submission: ContactSubmission }>> {
        return this.request<ApiResponse<{ submission: ContactSubmission }>>(`/admin/contact/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status }),
        });
    }

    async adminDeleteContact(id: number): Promise<ApiResponse<null>> {
        return this.request<ApiResponse<null>>(`/admin/contact/${id}`, {
            method: 'DELETE',
        });
    }

    // Admin Settings
    async adminGetSettings(): Promise<ApiResponse<{ settings: Record<string, string | null> }>> {
        return this.request<ApiResponse<{ settings: Record<string, string | null> }>>('/admin/settings');
    }

    async adminUpdateSettings(settings: Record<string, any>): Promise<ApiResponse<{ settings: Record<string, string | null> }>> {
        return this.request<ApiResponse<{ settings: Record<string, string | null> }>>('/admin/settings', {
            method: 'POST',
            body: JSON.stringify({ settings }),
        });
    }

    // Admin Audit Logs
    async adminGetAuditLogs(page: number = 1): Promise<ApiPaginatedResponse<AuditLog>> {
        return this.request<ApiPaginatedResponse<AuditLog>>(`/admin/audit?page=${page}`);
    }
}

export const api = new ApiService();
